// Structured weekly hours helpers shared by the editor and the guide.
// Mirrors backend/src/lib/teamsImport.ts formatWeekly so the display rows the
// diagram reads are regenerated identically on save.

import type { Weekday, WeeklyHours, TimeInterval, HolidayRange } from '../../api/client';

export const WEEKDAYS: Array<{ key: Weekday; label: string; short: string }> = [
  { key: 'mon', label: 'Monday', short: 'Mo' },
  { key: 'tue', label: 'Tuesday', short: 'Tu' },
  { key: 'wed', label: 'Wednesday', short: 'We' },
  { key: 'thu', label: 'Thursday', short: 'Th' },
  { key: 'fri', label: 'Friday', short: 'Fr' },
  { key: 'sat', label: 'Saturday', short: 'Sa' },
  { key: 'sun', label: 'Sunday', short: 'Su' },
];

// Accepts the short form the importer writes ("Mo") and full English names;
// anything else is rejected so "weekdays" never silently becomes Wednesday.
const DAY_TOKEN: Record<string, Weekday> = Object.fromEntries(
  WEEKDAYS.flatMap(d => [[d.short.toLowerCase(), d.key], [d.label.toLowerCase(), d.key], [d.label.slice(0, 3).toLowerCase(), d.key]]),
) as Record<string, Weekday>;

export function minutesToHHMM(min: number): string {
  if (min >= 1440) return '24:00';
  const h = String(Math.floor(min / 60)).padStart(2, '0');
  const m = String(min % 60).padStart(2, '0');
  return `${h}:${m}`;
}

export function hhmmToMinutes(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
  if (!m) return null;
  const h = Number(m[1]); const mi = Number(m[2]);
  if (h < 0 || h > 24 || mi < 0 || mi > 59 || (h === 24 && mi !== 0)) return null;
  return h * 60 + mi;
}

export function isEmptyWeekly(w?: WeeklyHours): boolean {
  return !w || WEEKDAYS.every(d => !(w[d.key] && w[d.key]!.length));
}

/** Display rows ("Mo-Fr 08:00–17:00") grouped by identical consecutive days;
 *  closed days get no row. */
export function formatWeekly(weekly?: WeeklyHours): Array<{ days: string; open: string; close: string }> {
  if (!weekly) return [];
  const keyOf = (k: Weekday) => JSON.stringify(weekly[k] || []);
  const rows: Array<{ days: string; open: string; close: string }> = [];
  let i = 0;
  while (i < WEEKDAYS.length) {
    let j = i;
    while (j + 1 < WEEKDAYS.length && keyOf(WEEKDAYS[j + 1].key) === keyOf(WEEKDAYS[i].key)) j++;
    const business = weekly[WEEKDAYS[i].key] || [];
    const label = i === j ? WEEKDAYS[i].short : `${WEEKDAYS[i].short}-${WEEKDAYS[j].short}`;
    for (const [start, end] of business) rows.push({ days: label, open: minutesToHHMM(start), close: minutesToHHMM(end) });
    i = j + 1;
  }
  return rows.slice(0, 10);
}

/** Best-effort parse of the legacy free-text rows ("Mo-Fr", "Sa", "Mo,We")
 *  written by earlier versions / by hand. Returns undefined when any row is
 *  not understood, so the editor never silently drops hours. */
export function parseLegacyHours(rows: Array<{ days: string; open: string; close: string }>): WeeklyHours | undefined {
  if (!rows.length) return undefined;
  const out: WeeklyHours = {};
  for (const row of rows) {
    const open = hhmmToMinutes(row.open); const close = hhmmToMinutes(row.close);
    if (open === null || close === null || open >= close) return undefined;
    const days: Weekday[] = [];
    for (const part of String(row.days || '').split(/[,;/ ]+/).filter(Boolean)) {
      const range = part.split(/[-–]/);
      if (range.length > 2) return undefined;
      const a = DAY_TOKEN[(range[0] || '').toLowerCase()];
      const b = range.length > 1 ? DAY_TOKEN[(range[1] || '').toLowerCase()] : a;
      if (!a || !b) return undefined;
      const ai = WEEKDAYS.findIndex(d => d.key === a); const bi = WEEKDAYS.findIndex(d => d.key === b);
      if (ai > bi) return undefined;
      for (let k = ai; k <= bi; k++) days.push(WEEKDAYS[k].key);
    }
    if (!days.length) return undefined;
    for (const d of days) {
      const list = (out[d] || []).concat([[open, close] as TimeInterval]).sort((x, y) => x[0] - y[0]);
      out[d] = list;
    }
  }
  return out;
}

/** Legacy holiday rows → ranges where the date is an ISO day; others are kept
 *  as-is by the caller. */
export function legacyHolidaysToRanges(rows: Array<{ name: string; date?: string }>): HolidayRange[] {
  const out: HolidayRange[] = [];
  for (const r of rows) {
    if (r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date)) out.push({ name: r.name, start: r.date, end: r.date });
  }
  return out;
}
